import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, BACKEND_URL, useAuth } from "../../App";
import { descargarArchivo } from "../../utils/download";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { 
  Plus, 
  Search, 
  PartyPopper,
  Eye,
  Calendar,
  MapPin,
  Users,
  Clock,
  Trash2,
  Download,
  Copy,
  MessageCircle
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import PageHeader from "../../components/PageHeader";
import StatusBadge from "../../components/StatusBadge";

const Eventos = () => {
  const { user } = useAuth();
  // Iter124 · P0 · Rol solo-lectura-asignada (mesero/chef/cliente) no puede crear eventos
  const esRolSoloLectura = ["mesero", "chef", "cliente"].includes((user?.workspace_rol || "").toLowerCase());
  const [eventos, setEventos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEstatus, setFilterEstatus] = useState("todos");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    cliente_id: "",
    nombre: "",
    tipo: "",
    fecha: "",
    hora_inicio: "",
    hora_fin: "",
    ubicacion: "",
    google_maps: "",
    tema: "",
    colorimetria: "",
    unidades: [],  // ← MULTI-UNIDAD: puede ser 1 o varias (integral)
  });

  const UNIDADES_DISPONIBLES = [
    { slug: "petit", nombre: "Celebra Petit", color: "#059669" },
    { slug: "kermesita", nombre: "La Kermesita", color: "#e11d48" },
    { slug: "bocatto", nombre: "Bocatto", color: "#ea580c" },
  ];

  const toggleUnidad = (slug) => {
    setFormData(prev => {
      const arr = prev.unidades || [];
      return { ...prev, unidades: arr.includes(slug) ? arr.filter(x => x !== slug) : [...arr, slug] };
    });
  };

  const estatuses = [
    { value: "nuevo", label: "Nuevo", color: "badge-pending" },
    { value: "cotizado", label: "Cotizado", color: "badge-design" },
    { value: "contratado", label: "Contratado", color: "badge-design" },
    { value: "confirmado", label: "Confirmado", color: "badge-validated" },
    { value: "en_produccion", label: "En producción", color: "badge-design" },
    { value: "en_evento", label: "En evento", color: "badge-event" },
    { value: "finalizado", label: "Finalizado", color: "badge-delivered" },
    { value: "cancelado", label: "Cancelado", color: "bg-red-100 text-red-800" },
  ];

  const fetchData = useCallback(async () => {
    try {
      const [eventosRes, clientesRes] = await Promise.all([
        axios.get(`${API}/eventos`),
        axios.get(`${API}/clientes`)
      ]);
      setEventos(eventosRes.data);
      setClientes(clientesRes.data);
    } catch {
      toast.error("Error al cargar eventos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClienteChange = (clienteId) => {
    const cliente = clientes.find(c => c.cliente_id === clienteId);
    if (cliente) {
      setFormData(prev => ({
        ...prev,
        cliente_id: clienteId,
        nombre: `${cliente.tipo_evento} de ${cliente.nombre_festejado}`,
        tipo: cliente.tipo_evento,
        fecha: cliente.fecha_evento,
        hora_inicio: cliente.hora_inicio || "",
        hora_fin: cliente.hora_fin || "",
        ubicacion: cliente.ubicacion || "",
        google_maps: cliente.google_maps || "",
        tema: cliente.tema || "",
        colorimetria: cliente.colorimetria || ""
      }));
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/eventos/${deleteId}`);
      toast.success("Evento eliminado");
      await fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    } finally {
      setDeleteId(null);
    }
  };

  const handleDescargarPDF = async (eventoId) => {
    toast.info("Generando PDF del expediente...");
    try {
      const response = await axios.get(`${API}/eventos/${eventoId}/pdf`);
      descargarArchivo(response.data.url, response.data.filename);
      toast.success("PDF descargado");
    } catch (error) {
      toast.error("Error al generar PDF");
    }
  };

  const handleDuplicar = async (eventoId) => {
    try {
      await axios.post(`${API}/eventos/${eventoId}/duplicar`);
      toast.success("Evento duplicado");
      await fetchData();
    } catch (error) {
      toast.error("Error al duplicar");
    }
  };

  const handleSave = async () => {
    if (!formData.cliente_id || !formData.nombre || !formData.fecha) {
      toast.error("Completa los campos requeridos");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      await axios.post(`${API}/eventos`, formData);
      toast.success("Evento creado");
      setFormData({
        cliente_id: "",
        nombre: "",
        tipo: "",
        fecha: "",
        hora_inicio: "",
        hora_fin: "",
        ubicacion: "",
        google_maps: "",
        tema: "",
        colorimetria: "",
        unidades: [],
      });
      await fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error("Error al crear el evento");
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const badge = estatuses.find(s => s.value === status);
    return badge || { label: status, color: "badge" };
  };

  // Vistas rápidas por fecha (Fase 5 sprint estabilización)
  const [vista, setVista] = useState("proximos"); // proximos | hoy | semana | mes | finalizados | cancelados | todos
  const hoyStr = new Date().toISOString().split('T')[0];
  const semanaStr = new Date(Date.now() + 7*86400000).toISOString().split('T')[0];
  const mesStr = new Date(Date.now() + 30*86400000).toISOString().split('T')[0];

  const filteredEventos = eventos
    .filter(evt => {
      // Vista rápida
      if (vista === "hoy" && evt.fecha !== hoyStr) return false;
      if (vista === "semana" && (evt.fecha < hoyStr || evt.fecha > semanaStr)) return false;
      if (vista === "mes" && (evt.fecha < hoyStr || evt.fecha > mesStr)) return false;
      if (vista === "proximos" && (evt.fecha < hoyStr || ["cancelado","finalizado","borrador"].includes(evt.estatus))) return false;
      if (vista === "finalizados" && evt.estatus !== "finalizado") return false;
      if (vista === "cancelados" && evt.estatus !== "cancelado") return false;
      // Filtros clásicos
      const matchesSearch = !searchTerm || (evt.nombre || "").toLowerCase().includes(searchTerm.toLowerCase()) || (evt.cliente_nombre || "").toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterEstatus === "todos" || evt.estatus === filterEstatus;
      return matchesSearch && matchesStatus;
    })
    // Ordenar SIEMPRE por fecha del evento ASC (Fase 5 sprint estabilización)
    .sort((a, b) => {
      if (vista === "finalizados" || vista === "cancelados") {
        return (b.fecha || "").localeCompare(a.fecha || "");
      }
      return (a.fecha || "9999-12-31").localeCompare(b.fecha || "9999-12-31");
    });

  // Stats
  const proximosEventos = eventos.filter(e => e.fecha >= new Date().toISOString().split('T')[0] && e.estatus !== 'finalizado' && e.estatus !== 'cancelado').length;

  return (
    <div className="space-y-6" data-testid="eventos-page">
      <PageHeader
        title="Eventos"
        description={esRolSoloLectura
          ? "Aquí ves solo los eventos donde estás asignado. Consulta datos, agenda y hoja operativa; las acciones de creación y edición corresponden al administrador."
          : "Cada evento es el expediente operativo completo. Desde aqui creas la hoja operativa, registras compras, asignas personal y cierras financiero."}
        primaryAction={esRolSoloLectura ? null : {
          label: "Nuevo evento",
          icon: Plus,
          testId: "btn-nuevo-evento",
          onClick: () => setDialogOpen(true),
        }}
      />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <Card className="border-stone-200 min-w-0">
          <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-4 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 bg-[#C89B7B]/20 rounded-full flex items-center justify-center">
              <PartyPopper className="w-4 h-4 sm:w-5 sm:h-5 text-[#C89B7B]" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] sm:text-xs text-stone-500 uppercase tracking-wider truncate">Total</p>
              <p className="font-display text-xl sm:text-2xl text-stone-900">{eventos.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-stone-200 min-w-0">
          <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-4 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 bg-rose-100 rounded-full flex items-center justify-center">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-rose-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] sm:text-xs text-stone-500 uppercase tracking-wider truncate">Próximos</p>
              <p className="font-display text-xl sm:text-2xl text-stone-900">{proximosEventos}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-stone-200 min-w-0">
          <CardContent className="p-3 sm:p-4 flex items-center gap-2 sm:gap-4 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 shrink-0 bg-blue-100 rounded-full flex items-center justify-center">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] sm:text-xs text-stone-500 uppercase tracking-wider truncate">Nuevos</p>
              <p className="font-display text-xl sm:text-2xl text-stone-900">
                {eventos.filter(e => e.estatus === 'nuevo' || e.estatus === 'planeacion').length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Vistas rápidas (Fase 5 sprint estabilización) */}
      <div className="flex flex-wrap gap-2" data-testid="vistas-rapidas">
        {[
          { key: "hoy",         label: "Hoy",         count: eventos.filter(e => e.fecha === hoyStr && !["cancelado","borrador"].includes(e.estatus)).length },
          { key: "semana",      label: "Esta semana", count: eventos.filter(e => e.fecha >= hoyStr && e.fecha <= semanaStr && !["cancelado","borrador"].includes(e.estatus)).length },
          { key: "mes",         label: "Este mes",    count: eventos.filter(e => e.fecha >= hoyStr && e.fecha <= mesStr && !["cancelado","borrador"].includes(e.estatus)).length },
          { key: "proximos",    label: "Próximos",    count: eventos.filter(e => e.fecha >= hoyStr && !["cancelado","finalizado","borrador"].includes(e.estatus)).length },
          { key: "finalizados", label: "Finalizados", count: eventos.filter(e => e.estatus === "finalizado").length },
          { key: "cancelados",  label: "Cancelados",  count: eventos.filter(e => e.estatus === "cancelado").length },
          { key: "todos",       label: "Todos",       count: eventos.length },
        ].map(v => (
          <button key={v.key} onClick={() => setVista(v.key)}
            data-testid={`vista-${v.key}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
              vista === v.key
                ? "bg-rose-900 text-white border-rose-900"
                : "bg-white text-stone-700 border-stone-300 hover:border-rose-400"
            }`}>
            {v.label} <span className="ml-1 opacity-70">({v.count})</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input 
            placeholder="Buscar evento..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-stone-300"
            data-testid="search-eventos"
          />
        </div>
        <Select value={filterEstatus} onValueChange={setFilterEstatus}>
          <SelectTrigger className="w-full md:w-48 border-stone-300" data-testid="filter-estatus-eventos">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            {estatuses.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Eventos List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-rose-900 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredEventos.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredEventos.map(evt => {
            const cliente = clientes.find(c => c.cliente_id === evt.cliente_id);
            const badge = getStatusBadge(evt.estatus);
            
            return (
              <Card key={evt.evento_id} className="border-stone-200 hover:shadow-lg transition-shadow cursor-pointer" data-testid={`evento-card-${evt.evento_id}`} onClick={(e) => {
                // Evitar navegar si se toca un botón interno
                if (e.target.closest('button, a')) return;
                window.location.href = `/panel/eventos/${evt.evento_id}`;
              }}>
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between gap-2 mb-3 min-w-0">
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-base sm:text-lg text-stone-900 truncate">{evt.nombre}</p>
                      <p className="text-xs sm:text-sm text-stone-500 truncate">{cliente?.nombre || "Sin cliente"}</p>
                    </div>
                    <span className={`badge shrink-0 ${badge.color}`}>{badge.label}</span>
                  </div>

                  <div className="space-y-1.5 text-xs sm:text-sm mb-3">
                    <div className="flex items-center gap-2 text-stone-600">
                      <Calendar className="w-3.5 h-3.5 shrink-0" />
                      <span>{evt.fecha}{evt.hora_inicio ? ` · ${evt.hora_inicio}${evt.hora_fin ? "–" + evt.hora_fin : ""}` : ""}</span>
                    </div>
                    {evt.ubicacion && (() => {
                      const isUrl = /^https?:\/\//i.test(String(evt.ubicacion).trim());
                      const lugar = evt.lugar_nombre || evt.nombre_lugar;
                      return (
                        <div className="flex items-center gap-2 text-stone-600 min-w-0">
                          <MapPin className="w-3.5 h-3.5 shrink-0" />
                          {isUrl ? (
                            <a href={evt.ubicacion} target="_blank" rel="noopener noreferrer"
                               className="text-rose-700 hover:underline truncate"
                               onClick={(e) => e.stopPropagation()}
                               data-testid={`btn-abrir-ubicacion-${evt.evento_id}`}>
                              {lugar || "Abrir ubicación"}
                            </a>
                          ) : (
                            <span className="truncate">{lugar || evt.ubicacion}</span>
                          )}
                        </div>
                      );
                    })()}
                    {(() => {
                      const total = (parseInt(evt.adultos) || 0) + (parseInt(evt.ninos) || 0);
                      if (total <= 0) return null;
                      return (
                        <div className="flex items-center gap-2 text-stone-600">
                          <Users className="w-3.5 h-3.5 shrink-0" />
                          {total} invitados
                        </div>
                      );
                    })()}
                    {Array.isArray(evt.unidades) && evt.unidades.length > 0 && (
                      <div className="flex items-center gap-1 flex-wrap mt-1" data-testid={`unidades-${evt.evento_id}`}>
                        {evt.unidades.map(u => (
                          <span key={u} className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-stone-100 text-stone-700 border border-stone-200">
                            {u}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Link to={`/panel/eventos/${evt.evento_id}`} className="flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
                      <Button className="w-full btn-primary text-xs" data-testid={`btn-ver-evento-${evt.evento_id}`}>
                        <Eye className="w-3 h-3 mr-1" />
                        Abrir evento
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); handleDescargarPDF(evt.evento_id); }} 
                      className="border-stone-300 text-xs"
                      data-testid={`btn-pdf-evento-${evt.evento_id}`}
                      aria-label="Descargar PDF"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      PDF
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); handleDuplicar(evt.evento_id); }} 
                      className="border-stone-300 text-xs"
                      data-testid={`btn-duplicar-evento-${evt.evento_id}`}
                      aria-label="Duplicar evento"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Duplicar
                    </Button>
                    {cliente?.whatsapp && (() => {
                      const url = require("../../lib/whatsapp").whatsappUrl(cliente.whatsapp, `Hola ${cliente.nombre}, te escribo sobre el evento: ${evt.nombre} (${evt.fecha})`);
                      if (!url) return null;
                      return (
                        <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid={`btn-wa-evento-${evt.evento_id}`} aria-label="WhatsApp">
                            <MessageCircle className="w-3 h-3 mr-1" />
                            WA
                          </Button>
                        </a>
                      );
                    })()}
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setDeleteId(evt.evento_id); }} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`btn-delete-evento-${evt.evento_id}`} aria-label="Eliminar">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <PartyPopper className="w-8 h-8 text-stone-400" />
          </div>
          <h3 className="font-display text-xl text-stone-900 mb-2">No hay eventos</h3>
          <p className="text-stone-600">Crea tu primer expediente de evento</p>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl w-[calc(100vw-1rem)] max-h-[92vh] p-0 flex flex-col overflow-hidden" data-testid="eventos-dialog">
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-stone-200 flex-shrink-0">
            <DialogTitle className="font-display text-xl">Nuevo Evento</DialogTitle>
            <DialogDescription>Al crear el evento se abrirá su expediente para asignar personal, servicios y menú.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 px-5 flex-1 overflow-y-auto">
            {/* From Cliente */}
            <div className="space-y-2">
              <Label>Crear desde cliente existente *</Label>
              <Select 
                value={formData.cliente_id} 
                onValueChange={handleClienteChange}
              >
                <SelectTrigger className="border-stone-300" data-testid="select-cliente-evento">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map(cliente => (
                    <SelectItem key={cliente.cliente_id} value={cliente.cliente_id}>
                      {cliente.nombre} - {cliente.tipo_evento}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre del evento *</Label>
              <Input 
                id="nombre"
                value={formData.nombre}
                onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
                className="border-stone-300"
                data-testid="input-nombre-evento"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo</Label>
                <Input 
                  id="tipo"
                  value={formData.tipo}
                  onChange={(e) => setFormData(prev => ({ ...prev, tipo: e.target.value }))}
                  className="border-stone-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fecha">Fecha *</Label>
                <Input 
                  id="fecha"
                  type="date"
                  value={formData.fecha}
                  onChange={(e) => setFormData(prev => ({ ...prev, fecha: e.target.value }))}
                  className="border-stone-300"
                  data-testid="input-fecha-evento"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="hora_inicio">Hora inicio</Label>
                <Input 
                  id="hora_inicio"
                  type="time"
                  value={formData.hora_inicio}
                  onChange={(e) => setFormData(prev => ({ ...prev, hora_inicio: e.target.value }))}
                  className="border-stone-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hora_fin">Hora fin</Label>
                <Input 
                  id="hora_fin"
                  type="time"
                  value={formData.hora_fin}
                  onChange={(e) => setFormData(prev => ({ ...prev, hora_fin: e.target.value }))}
                  className="border-stone-300"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="ubicacion">Ubicación</Label>
              <Input 
                id="ubicacion"
                value={formData.ubicacion}
                onChange={(e) => setFormData(prev => ({ ...prev, ubicacion: e.target.value }))}
                className="border-stone-300"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tema">Tema</Label>
                <Input 
                  id="tema"
                  value={formData.tema}
                  onChange={(e) => setFormData(prev => ({ ...prev, tema: e.target.value }))}
                  className="border-stone-300"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="colorimetria">Colorimetría</Label>
                <Input 
                  id="colorimetria"
                  value={formData.colorimetria}
                  onChange={(e) => setFormData(prev => ({ ...prev, colorimetria: e.target.value }))}
                  className="border-stone-300"
                />
              </div>
            </div>

            {/* Unidades (multi-select) — si son >1 → Evento Integral */}
            <div className="space-y-2 pt-1">
              <Label>Unidades participantes</Label>
              <p className="text-xs text-stone-500 -mt-1">Selecciona 1 unidad para un evento único, o varias para un <b className="text-purple-700">Evento Integral</b>.</p>
              <div className="flex flex-wrap gap-2" data-testid="unidades-selector">
                {UNIDADES_DISPONIBLES.map(u => {
                  const activo = (formData.unidades || []).includes(u.slug);
                  return (
                    <button
                      key={u.slug}
                      type="button"
                      onClick={() => toggleUnidad(u.slug)}
                      data-testid={`chk-unidad-${u.slug}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border-2 transition-colors ${activo ? "text-white" : "bg-white text-stone-700"}`}
                      style={{
                        borderColor: u.color,
                        background: activo ? u.color : undefined,
                      }}
                    >
                      {activo ? "✓ " : "○ "}{u.nombre}
                    </button>
                  );
                })}
              </div>
              {(formData.unidades || []).length > 1 && (
                <div className="mt-1 inline-block px-2 py-1 rounded bg-purple-100 text-purple-800 text-xs font-medium" data-testid="badge-integral">
                  ✨ Evento Integral · {(formData.unidades || []).length} unidades bloquean la fecha
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="px-5 py-3 border-t border-stone-200 bg-white flex-shrink-0 gap-2 sm:gap-0 flex-col-reverse sm:flex-row">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-stone-300 w-full sm:w-auto">
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary w-full sm:w-auto" data-testid="btn-guardar-evento">
              {saving ? "Guardando..." : "Crear Evento"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar evento?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminarán también los recordatorios y compras asociadas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Eventos;
